import { createFileRoute, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { Card } from "@/components/ui/card"
import { Search, Copy, RefreshCw } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Api, copyGigDataToClipboard, getLspName } from '@/lib/utils'
import { Order } from '@/lib/interfaces'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { useEffect, useState } from 'react'
import { DataTable, gigStatusStyles, orderGigTimeRange } from '@/components/features/orders/data-table'
import { columns } from '@/components/features/orders/columns'
import { SearchGigDialog } from '@/components/features/gigs/search-gig'
import Tooltip from '@/components/ui/tooltip'

// Every order under a gig shares the same LSP (already shown once, at the gig level, in this
// page's own header card above) - repeating it as a per-order column here is redundant. Filtered
// out of the Orders page's own `columns` rather than removing it there, since that page still
// needs it (it can show orders from multiple different gigs/LSPs at once).
const gigOrderColumns = columns.filter(col => !('accessorKey' in col && col.accessorKey === 'lsp'))

// The subset of /webui/gig/info's `gig` object this page actually uses - it carries a lot more
// (checkedinAt, waitingMins/pickupMins/deliveryMins, ...) that isn't part of what this page
// shows, so isn't reproduced here.
type GigDetails = {
    gigId: string
    storeId?: string
    pickupSlotStart?: string
    pickupSlotEnd?: string
    riderName?: string
    riderNumber?: string
    gigStatus?: string
    adherenceStatus?: string
    bppId?: string
    lspPayout?: number
    totalWorkingDuration?: number
}

export const Route = createFileRoute('/_auth/gigs/$gigId')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { search: boolean } => {
        return {
            search: search.search as boolean || false
        }
    }
})

function RouteComponent() {
    const { gigId } = Route.useParams()
    const { search } = Route.useSearch()
    const [showSearchGig, setSearchGigDisplay] = useState(false)
    const [errMsg, setErrMsg] = useState<string | undefined>(undefined)

    const { token, accountId, role, setPage, loggedIn, isRetail, unfulfillEnabled } = useStore(useShallow(state => ({
        token: state.token,
        accountId: state.selectedAccount.id,
        role: state.selectedAccount.role,
        setPage: state.setPage,
        loggedIn: state.loggedIn,
        isRetail: state.selectedAccount.is_retail,
        unfulfillEnabled: state.selectedAccount.unfulfil_enabled,
    })))

    const getGigInfo = useQuery({
        queryKey: ['gigInfo', gigId],
        queryFn: () => Api<{ status: number, gig: GigDetails, orders: Order[] }>('/webui/gig/info', 'POST', { id: gigId }, { token }),
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn && gigId !== '<gigId>',
        retry: 1,
    })

    const gig = getGigInfo.data?.gig

    // DataTable's gig-group header derives everything (window, status, LSP payout) from fields
    // on each Order row - but /webui/gig/info puts those at the gig level, once, not repeated
    // onto every order under it. Stamp them onto each order before handing the list to
    // DataTable, so it can reuse its existing derivation logic unmodified rather than this page
    // needing a separate rendering path just for the same header info.
    const orders: Order[] = (getGigInfo.data?.orders ?? []).map(order => ({
        ...order,
        pickupSlotStart: order.pickupSlotStart ?? gig?.pickupSlotStart,
        pickupSlotEnd: order.pickupSlotEnd ?? gig?.pickupSlotEnd,
        gigStatus: order.gigStatus ?? gig?.gigStatus,
        lspPayout: order.lspPayout ?? gig?.lspPayout,
    }))

    useEffect(() => {
        setPage('Search Gig')
    }, [])

    useEffect(() => {
        if (search) {
            setSearchGigDisplay(true)
        }
    }, [search])

    useEffect(() => {
        if (getGigInfo.error) {
            setErrMsg('Error fetching gig details.')
            setSearchGigDisplay(true)
        }
    }, [getGigInfo.error])

    const navigate = useNavigate({ from: Route.fullPath })

    return <div className='w-full px-5 py-2 *:mb-6'>
        <Card className='p-0'>
            <div className='bg-[#c2e0f0] rounded-t-xl py-4 flex items-center justify-between px-5'>
                <div className='flex items-center gap-2'>
                    {/* The url param can be whatever was searched (order id/client order id/gig
                    id, per the search box) - once the gig resolves, always show its actual
                    gigId here instead, so this never displays an order id by accident. */}
                    <p className='font-semibold lg:text-lg'>Gig {gig?.gigId ?? gigId}</p>
                    <Search className='w-5 h-5 cursor-pointer' stroke='black' strokeWidth={3} onClick={() => setSearchGigDisplay(true)} />
                </div>
                {gig ? (
                    <div className='flex items-center gap-2'>
                        {gig.gigStatus ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${gigStatusStyles[gig.gigStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                {gig.gigStatus}
                            </span>
                        ) : null}
                        <Tooltip title="Copy gig details">
                            <Copy
                                className='w-4 h-4 cursor-pointer text-gray-700 hover:text-black'
                                onClick={async () => {
                                    const didCopy = await copyGigDataToClipboard({
                                        gigId: gig.gigId,
                                        gigTime: orderGigTimeRange(gig),
                                        riderName: gig.riderName || gig.riderNumber,
                                        gigStatus: gig.gigStatus,
                                        lspPayout: gig.lspPayout,
                                        orders,
                                    })
                                    if (didCopy) {
                                        toast.success('Gig details copied to clipboard')
                                    } else {
                                        toast.error('Failed to copy gig details')
                                    }
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Refresh gig details">
                            <RefreshCw
                                className={`w-4 h-4 cursor-pointer text-gray-700 hover:text-black ${getGigInfo.isFetching ? 'animate-spin' : ''}`}
                                onClick={() => getGigInfo.refetch()}
                            />
                        </Tooltip>
                    </div>
                ) : null}
            </div>
            {gig ? (
                <div className='py-3 px-5 grid gap-4 grid-cols-2 sm:grid-cols-4'>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Store ID</p>
                        <p className='font-medium text-sm'>{gig.storeId || '--'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>LSP</p>
                        <p className='font-medium text-sm'>{gig.bppId ? getLspName(gig.bppId) : '--'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Working Duration</p>
                        <p className='font-medium text-sm'>{gig.totalWorkingDuration != null ? `${gig.totalWorkingDuration} min` : '--'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Adherence Status</p>
                        <p className='font-medium text-sm'>{gig.adherenceStatus || '--'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Delivered</p>
                        <p className='font-medium text-sm'>{orders.filter(o => o.orderState === 'Order-delivered').length}/{orders.length}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Timing</p>
                        <p className='font-medium text-sm'>{orderGigTimeRange(gig) || '--'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>Rider</p>
                        <p className={`font-medium text-sm ${gig.riderName || gig.riderNumber ? '' : 'text-gray-400 italic'}`}>{gig.riderName || gig.riderNumber || 'Rider not assigned'}</p>
                    </div>
                    <div>
                        <p className='text-sm text-slate-500 font-medium'>LSP Payout</p>
                        <p className='font-medium text-sm'>{gig.lspPayout != null ? `₹${gig.lspPayout}` : '--'}</p>
                    </div>
                </div>
            ) : null}
        </Card>

        {gigId !== '<gigId>' ? (
            getGigInfo.isLoading ? (
                <p className='text-sm text-gray-500'>Loading…</p>
            ) : orders.length > 0 ? (
                <DataTable
                    accountId={accountId}
                    columns={gigOrderColumns}
                    data={orders}
                    isFetching={getGigInfo.isFetching}
                    unfulfillEnabled={!!unfulfillEnabled}
                    isSuperAdmin={/super_admin/.test(role)}
                    isRetail={!!isRetail}
                    onSuccess={() => getGigInfo.refetch()}
                />
            ) : !getGigInfo.isError ? (
                <p className='text-sm text-gray-400'>No orders found for this gig.</p>
            ) : null
        ) : null}

        <SearchGigDialog open={showSearchGig} onClose={(resetSearch) => {
            setSearchGigDisplay(false)
            if (resetSearch) {
                navigate({
                    search: () => ({
                        search: false
                    })
                })
            }
        }} errMsg={errMsg} clearErrMsg={() => setErrMsg(undefined)} searchedGigId={gigId !== '<gigId>' ? gigId : undefined} />
    </div>
}
